import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DescribeIssue from './DescribeIssue';
import workAnalyzerService from '../services/workAnalyzerService';
import { Issue } from '../models/Issue';
import IssuePromptComponent from '../components/IssuePromptComponent';
import IssueAnswerComponent from '../components/IssueAnswerComponent';
import { Container, Box } from '@mui/material';
import CustomLoadingComponent from '../components/CustomLoadingComponent';
import IssuePromptWithImageComponent from '../components/IssuePromptWithImageComponent';
import GlossarySidebarComponent from '../components/GlossarySidebarComponent';

const DescribeIssueParent: React.FC = () => {
    const { id: routeId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [answer, setAnswer] = useState<Issue | null>(null);
    const [loading, setLoading] = useState(false);
    const [isGlossaryOpen, setIsGlossaryOpen] = useState<boolean>(false);
    const [diagramUrl, setDiagramUrl] = useState<string | undefined>();

    useEffect(() => {
        const fetchAnswer = async (id: string) => {
            setLoading(true);
            const response = await workAnalyzerService.getRepairConversation(id); // Adjust this service call to your actual service method
            setAnswer(response);
            setLoading(false);
        };

        if (routeId) {
            fetchAnswer(routeId);
        }
    }, [routeId]);

    const toggleGlossary = async () => {
        if(routeId && !isGlossaryOpen && !diagramUrl && answer?.Glossary){
            const diagramTerm = answer?.Glossary.find(term => term.word.toLowerCase() === 'diagram');
            if (diagramTerm) {
                const imageResponse = await workAnalyzerService.getGlossaryImage(diagramTerm, routeId);
                setDiagramUrl(imageResponse ?? undefined);
            }
        }
        setIsGlossaryOpen(!isGlossaryOpen);
    }

    const handleNewIssueSubmit = async (description: string) => {
        setLoading(true);
        const response = await workAnalyzerService.repairInstructions(description);
        setAnswer(response?.conversationResponse ?? null);
        setLoading(false);
        navigate(`/repair/${response?.conversation.id}`); // Assuming response contains an id
    };

    const answerOutstandingQuestion = async (optionIndex: number) => {
        const selectedOption = answer?.outstandingQuestion.Options[optionIndex];
        if (!selectedOption) {
            console.log("No selected option.");
            return;
        }
        setLoading(true);
        const response = await workAnalyzerService.repairInstructions(selectedOption);
        setAnswer(response?.conversationResponse ?? null);
        setLoading(false);
    };

    const onUpload = async (file: File, text: string) => {
        setLoading(true);
        const result = await workAnalyzerService.upload(file);
        const response = await workAnalyzerService.repairInstructionsWithImage(text, result.id);
        setAnswer(response?.conversationResponse ?? null);
        setLoading(false);
    };

    const handleIssueSubmit = async (description: string) => {
        setLoading(true);
        const response = await workAnalyzerService.repairInstructions(description);
        setAnswer(response?.conversationResponse ?? null);
        setLoading(false);
    };

    const onImageSkip = async () => {
        setLoading(true);
        const response = await workAnalyzerService.repairInstructions("I cannot upload a picture. Please do not ask again.");
        setAnswer(response?.conversationResponse ?? null);
        setLoading(false);
    }

    const prompt = "In a short sentence describe your issue.";

    return (
        <>
            {loading && (
                <Container component="main" maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ mt: 4, width: '100%' }}>
                        <CustomLoadingComponent />
                    </Box>
                </Container>
            )}
            {!loading && !answer && (
                <DescribeIssue prompt={prompt} onSubmit={handleNewIssueSubmit} />
            )}
            {!loading && answer && answer.HaveAnswer && routeId && (
                <IssueAnswerComponent issue={answer} openGlossary={toggleGlossary} conversationId={routeId}/>
            )}
            {!loading && answer && !answer.HaveAnswer && (
                answer.outstandingQuestion?.NeedPicture ? (
                    <IssuePromptWithImageComponent onSkip={onImageSkip} issue={answer} onUpload={onUpload} />
                ) : answer.outstandingQuestion?.Options.length > 0 ? (
                    <IssuePromptComponent issue={answer} onAnswerSelect={answerOutstandingQuestion} />
                ) : 
                <DescribeIssue prompt={answer.outstandingQuestion?.QuestionText ?? "Can you provide more information?"} onSubmit={handleIssueSubmit} />
            )}
            
            { routeId && answer && answer.Glossary ? (
                <GlossarySidebarComponent
                    isOpen={isGlossaryOpen}
                    conversationId={routeId}
                    toggleDrawer={toggleGlossary}
                    glossary={answer?.Glossary}
                    diagramImageUrl={diagramUrl}
                /> ): null
            }
        </>
    );
};

export default DescribeIssueParent;
